import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Skeleton,
} from "@facade/components/ui";
import { getInitials, splitFullName } from "./utils";
import { useEffect, useState } from "react";

import { UserAvatarProps } from "./UserAvatar.types";
import { cn } from "@facade/lib/utils";
import { useAttachmentDownloadUrlQuery } from "@facade/queries";

function UserAvatar({
  firstName,
  lastName,
  name,
  pictureAttachmentId,
  email,
  avatarImageVariation = "small",
  context,
  isLoading = false,
  className,
  ...props
}: UserAvatarProps) {
  const {
    data: pictureDownloadUrl,
    isLoading: isLoadingPictureUrl,
    isFetching: isFetchingPictureUrl,
    isError: isPictureUrlError,
  } = useAttachmentDownloadUrlQuery(
    pictureAttachmentId,
    avatarImageVariation === "small" ? "sm" : "original",
    context,
  );

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageFailed(false);
  }, [pictureDownloadUrl?.url]);

  const fullName = splitFullName(name);

  const resolvedFirstName =
    firstName ?? fullName.firstName ?? email?.split("@")[0];
  const resolvedLastName = lastName ?? fullName.lastName;

  const initials = getInitials(resolvedFirstName, resolvedLastName);

  const accessibleName =
    (name ?? [resolvedFirstName, resolvedLastName].filter(Boolean).join(" ")) ||
    email ||
    "User avatar";

  const hasPicture = Boolean(pictureAttachmentId);
  const hasPictureUrl = Boolean(pictureDownloadUrl?.url);

  const isResolvingPicture =
    hasPicture &&
    !isPictureUrlError &&
    (isLoadingPictureUrl || isFetchingPictureUrl || !hasPictureUrl);

  const isLoadingImage = hasPictureUrl && !imageLoaded && !imageFailed;
  const shouldShowSkeleton = isLoading || isResolvingPicture || isLoadingImage;
  const shouldRenderImage = hasPictureUrl && !imageFailed;

  return (
    <Avatar
      aria-label={accessibleName}
      className={cn("after:border-transparent", className)}
      {...props}
    >
      {shouldRenderImage && (
        <AvatarImage
          className="after:border-transparent"
          src={pictureDownloadUrl?.url}
          alt={accessibleName}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
        />
      )}

      <AvatarFallback
        className={cn(
          "overflow-hidden text-sm font-semibold text-primary-foreground uppercase",
          !shouldShowSkeleton && "bg-brand-primary hover:bg-brand-accent/80",
          shouldShowSkeleton && "bg-transparent",
        )}
      >
        {shouldShowSkeleton ? (
          <Skeleton className="size-[calc(100%+2px)] rounded-full" />
        ) : (
          initials
        )}
      </AvatarFallback>
    </Avatar>
  );
}

export { UserAvatar };
